"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import OurHotelSearch from "./OurHotelSearch";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import Sidebar from "./Sidebar";
import "@/styles/header.css";
import "@/styles/globals.css";

export default function Header({
  onClick,
  brandId,
  brandColor,
  onClickHotel,
  onCloseBookingEngine,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const [ourHotelPopup, setOurHotelPopup] = useState(false);
  const popupRef = useRef(null);

  const headerImagesUrl = {
    1: "/images/headerLogos/MastiffLogo.png",
    2: "/images/headerLogos/KyriadLogo.png",
    3: "/images/headerLogos/CitrusLogo.png",
    5: "/images/headerLogos/mangohotelslogo.png",
    6: "/images/headerLogos/istaylogo.png",
    8: "/images/tab-logo6.png",
    9: "/images/tab-logo5.png",
  };

  const headerLogo = headerImagesUrl[brandId] || "/images/logo.png";

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ✅ Pages where only sidebar should be shown
  const sidebarOnlyPages = [""];
  const showSidebarOnly = sidebarOnlyPages.includes(pathname);

  const handleBookNowClick = () => {
    onClick?.();
  };

  return (
    <>
      <header>
        {/* Sidebar */}
        <Sidebar
          isOpen={menuOpen || showSidebarOnly}
          setIsOpen={setMenuOpen}
          forceOpen={showSidebarOnly}
          onClick={onClick}
        />

        {/* ============ VERSION 1: Sidebar Only (Home, Offers, Contact) ============ */}
        {showSidebarOnly ? (
          <nav
            className={`navbar navbar-expand-md navbar-light fixed-top ${
              isScrolled ? "navbar-scroll bg-white shadow-sm" : ""
            }`}
          >
            <div className="container-fluid d-flex align-items-center justify-content-between">
              {/* Left placeholder */}
              <div style={{ width: "100px" }}></div>

              {/* Center - Logo */}
              <Link
                href="/"
                className="logoimg"
                style={{ marginLeft: "130px" }}
              >
                <Image
                  src={headerLogo}
                  alt="Logo"
                  width={90}
                  height={40}
                  fetchPriority="high"
                />
              </Link>

              {/* Right - Book Now */}
              <div style={{ width: "100px" }} className="text-end">
                <Link
                  href="#"
                  className="box-btn know-more p-2 book-btn"
                  onClick={handleBookNowClick}
                >
                  Book Now
                </Link>
              </div>
            </div>
          </nav>
        ) : (
          /* ============ VERSION 2: Full Width Menu + Sidebar (Other Pages) ============ */
          <nav
            className={`navbar navbar-expand-md navbar-light fixed-top ${
              isScrolled ? "navbar-scroll bg-white shadow-sm" : ""
            }`}
          >
            <div className="container-fluid d-flex align-items-center justify-content-between">
              {/* Left - Sidebar Toggle (mobile) */}

              <div
                className="d-flex align-items-center d-md-none"
                style={{
                  width: "100%",
                  minHeight: "52px",
                  position: "relative",
                  justifyContent: "center",
                }}
              >
                {/* Center - Logo */}
                <Link
                  href="/"
                  className="logoimg"
                  style={{ display: "block", margin: "0 auto" }}
                >
                  <Image
                    src={headerLogo}
                    alt="Logo"
                    width={90}
                    height={40}
                    fetchPriority="high"
                  />
                </Link>
                {/* Book Now Button */}
                <div
                  className="text-end header-book-now-btn book-now-parent"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <a
                    className="box-btn know-more p-2 book-btn cursor-pointer"
                    href="#"
                    onClick={handleBookNowClick}
                    style={{ background: brandColor || "#f04e02" }}
                  >
                    Book Now
                  </a>
                </div>
              </div>

              {/* Full-width Menu (desktop only) */}
              {/* Full-width Menu (desktop only, with centered logo) */}
              <div className="mainDiv">
                {/* Left links */}
                <div className="leftDiv">
                  <div className="leftLinks">
                    <Link href="/" className="nav-link">
                      Home
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setOurHotelPopup((prev) => !prev);
                        // closeSidebar();
                        // onCloseBookingEngine();
                        onClickHotel?.();
                      }}
                      className="nav-link btn btn-link p-0"
                      style={{ background: "none", border: "none" }}
                    >
                      Hotels
                    </button>
                    <Link href="/our-brands" className="nav-link">
                      Brands
                    </Link>
                  </div>
                </div>

                {/* Center Logo */}
                <div className="centerLogo">
                  <Link href="/" className="logoimg">
                    <Image
                      src={headerLogo}
                      alt="Logo"
                      width={90}
                      height={40}
                      fetchPriority="high"
                    />
                  </Link>
                </div>

                {/* Right links */}
                <div className="rightDiv">
                  <div className="rightLinks">
                    <Link href="/orange-tiger-offers" className="nav-link">
                      Offers
                    </Link>
                    <Link href="/careers" className="nav-link">
                      Careers
                    </Link>
                    <Link href="/contact" className="nav-link">
                      Contact
                    </Link>
                  </div>

                  <div className="d-flex align-items-center gap-2 text-end header-book-now-btn me-2">
                    {/* <Link href="#" className="nav-link me-2">
                      Login/Join
                    </Link> */}

                    {/* Empty spacer */}
                    {!isScrolled && <div style={{ marginRight: "80px" }}></div>}

                    <a
                      className={`box-btn know-more p-2 book-btn cursor-pointer ${
                        isScrolled ? "" : "hidden"
                      }`}
                      href="#"
                      onClick={handleBookNowClick}
                      style={{ background: brandColor || "#f04e02" }}
                    >
                      Book Now
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </nav>
        )}
      </header>
      {ourHotelPopup && typeof window !== "undefined" &&
        createPortal(
          <div
            ref={popupRef}
            className="our-hotels-popup-for-data dropdown-menu show p-3 pt-0"
            style={{
              maxHeight: "85vh",
              overflowY: "auto",
              overflowX: "hidden",
              top: "77px",
              zIndex: "10001",
            }}
          >
            <OurHotelSearch onClick={onClick} />
            <button
              className="closebtnfor-our-hotel-popup"
              onClick={() => {
                setOurHotelPopup(false);
                onCloseBookingEngine?.();
              }}
            >
              <X />
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
